import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { usePayments } from './usePayments'
import type { Payment } from './usePayments'
import { useContacts } from '../contacts/useContacts'
import { useCryptoPrices } from '../../hooks/useCryptoPrices'
import { PaymentRow, PAYMENT_GRID_CLASS, PAYMENT_HEAD_CLASS } from './PaymentRow'
import { PaymentsReimportSection } from './PaymentsReimportSection'
import { PaymentFormModal } from './PaymentFormModal'
import { PaymentContactModal } from './PaymentContactModal'
import { DeleteConfirmModal } from '../contacts/DeleteConfirmModal'
import { useDeletePayment, paymentErrorCode } from './usePaymentMutations'
import { DemoGuardedButton } from '../../components/DemoGuardedButton'
import { toast } from '../../components/Toast/useToast'
import { PremiumRequired } from '../../components/PremiumRequired'

// Audit 2026-09-01 (Design-Paritaet Payments): Referenz payments.html kennt
// keinen Datumsfilter ueber der Tabelle (nur Pagination) — die vormals hier
// gerenderten nativen <input type="date">-Felder wichen als ungestyltes
// Browser-Default-UI vom Design ab und sind entfernt. usePayments()
// akzeptiert from/to weiterhin (Backend-Faehigkeit bleibt erhalten), die
// Seite ruft sie nur ohne aktiven Filter auf.
export function PaymentsPage() {
  const { t } = useTranslation(['payments', 'shell'])
  const [page, setPage] = useState(1)

  // R6/MP — Manual Payment: Erfassen/Bearbeiten/Loeschen (v1-Paritaet zu
  // app/views/payments/index.haml:9 + _payment.haml:60-79).
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null)
  const [editing, setEditing] = useState<Payment | null>(null)
  const [deleting, setDeleting] = useState<Payment | null>(null)
  const deletePayment = useDeletePayment()
  // P1 (v1-Paritaets-Audit): Contact-Korrektur auch fuer importierte Zahlungen.
  const [assigningContact, setAssigningContact] = useState<Payment | null>(null)

  // FIN-SWEEP-FE-2 (F2): isPremiumError suppresses the red error UI for
  // 403 responses (premium-gated). Mirror of InvoicesPage.tsx:144-146.
  const {
    data: payments,
    isLoading,
    isError,
    isPremiumError,
    totalPages,
  } = usePayments({
    page,
    perPage: 25,
    from: '',
    to: '',
  })

  // Audit 2026-09-01: Vorlage zeigt den Betrag konsequent in Fiat (EUR).
  // Importierte HNT-Zahlungen ohne eigenen Fremdwaehrungsbetrag rechnen wir
  // mit dem live HNT-Kurs (gleicher Hook wie WalletBalanceCards) um.
  const { prices, currency: fiatCurrency } = useCryptoPrices()

  async function handleConfirmDelete() {
    if (!deleting) return
    try {
      await deletePayment.mutateAsync(deleting.id)
      toast.success(t('deleteModal.toastSuccess'))
      setDeleting(null)
      // R6-Review F4: War das die letzte Zeile einer Folgeseite, gaebe es nach
      // dem Refetch eine leere Seite — deshalb eine Seite zurueckblaettern.
      if (payments.length === 1 && page > 1) setPage(page - 1)
    } catch (err) {
      toast.error(t(`errors.${paymentErrorCode(err)}`))
    }
  }

  // Audit 2026-09-01 (Design-Paritaet Payments): Vorlage zeigt in der Liste
  // den zugeordneten Menschen. Ein Batch-Load der ersten 100 Kontakte reicht
  // fuer die Namensaufloesung (gleiches Muster wie PaymentFormModal); seltene
  // Kontakte jenseits davon fallen auf "Kontakt #id" zurueck statt N Requests
  // pro Zeile auszuloesen.
  const { contacts } = useContacts({ perPage: 100 })
  const contactNames = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of contacts ?? []) map.set(c.id, c.name)
    return map
  }, [contacts])

  if (isPremiumError) {
    return <PremiumRequired />
  }

  return (
    <div>
      {/* Audit 2026-09-01: Breadcrumb "Premium > Payment History" (Referenz
          payments.html: HtLayout.mount breadcrumb: [{label:'Premium'},{label:'Payment History'}]),
          gleiches Muster wie SplitsLayout.tsx (splits-breadcrumb). */}
      <nav
        className="payments-breadcrumb"
        aria-label="Breadcrumb"
        data-testid="payments-breadcrumb"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: 'var(--text-subtle)',
          marginBottom: 14,
        }}
      >
        <span>{t('shell:nav.sections.premium')}</span>
        <span aria-hidden="true">›</span>
        <span style={{ color: 'var(--text)', fontWeight: 500 }}>{t('page.breadcrumbCurrent')}</span>
      </nav>

      <header
        className="section-header"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1>{t('page.title')}</h1>
          <p className="muted" style={{ maxWidth: 560 }}>
            {t('page.subtitle')}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <div className="row" style={{ gap: 8 }}>
            <PaymentsReimportSection />
            <DemoGuardedButton
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setEditing(null)
                setFormMode('create')
              }}
              data-testid="track-manual-payment-btn"
            >
              <Plus size={14} aria-hidden="true" />
              {t('actions.trackManual')}
            </DemoGuardedButton>
          </div>
        </div>
      </header>

      <section className="card">
        <div className={PAYMENT_HEAD_CLASS}>
          <div className="payment-col-md">{t('table.date')}</div>
          <div>{t('table.payment')}</div>
          <div className="payment-col-md">{t('table.person')}</div>
          <div style={{ textAlign: 'right' }}>{t('table.amount')}</div>
          <div />
        </div>

        {isLoading && (
          <div className={`${PAYMENT_GRID_CLASS} pulse`}>
            <div className="skeleton-text payment-col-md" style={{ width: '70%' }}></div>
            <div className="skeleton-text" style={{ width: '80%' }}></div>
            <div className="skeleton-text payment-col-md" style={{ width: '60%' }}></div>
            <div className="skeleton-text" style={{ width: '70px', justifySelf: 'end' }}></div>
            <div className="skeleton-text" style={{ width: '40px', justifySelf: 'end' }}></div>
          </div>
        )}

        {isError && !isLoading && (
          <div className="empty-state">
            <div
              className="muted"
              style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--danger)' }}
            >
              {t('states.error')}
            </div>
          </div>
        )}

        {!isLoading && !isError && payments.length === 0 && (
          <div className="empty-state">
            <div className="muted" style={{ padding: '40px 20px', textAlign: 'center' }}>
              {t('states.empty')}
            </div>
          </div>
        )}

        {!isLoading &&
          !isError &&
          payments.length > 0 &&
          payments.map((payment) => (
            <PaymentRow
              key={payment.id}
              payment={payment}
              contactName={
                payment.contact_id != null
                  ? (contactNames.get(payment.contact_id) ??
                    t('form.contactUnknown', { id: payment.contact_id }))
                  : null
              }
              hntPrice={prices.hnt}
              fiatCurrency={fiatCurrency}
              onEdit={(p) => {
                setEditing(p)
                setFormMode('edit')
              }}
              onDelete={(p) => setDeleting(p)}
              onAssignContact={(p) => setAssigningContact(p)}
            />
          ))}

        {totalPages > 1 && (
          <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t('pagination.previous')}
            </button>
            <span style={{ fontSize: '13px', display: 'flex', alignItems: 'center' }}>
              {t('pagination.page', { page, totalPages })}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('pagination.next')}
            </button>
          </div>
        )}
      </section>

      <PaymentFormModal
        isOpen={formMode !== null}
        mode={formMode ?? 'create'}
        payment={editing}
        onClose={() => {
          setFormMode(null)
          setEditing(null)
        }}
        /* R6-Review F4: Nur eine NEUE Zahlung springt auf Seite 1 (sie landet
           dort oben). Beim Bearbeiten bleibt der Nutzer auf seiner Seite. */
        onSaved={() => {
          if (formMode === 'create') setPage(1)
        }}
      />

      {assigningContact && (
        <PaymentContactModal payment={assigningContact} onClose={() => setAssigningContact(null)} />
      )}

      <DeleteConfirmModal
        isOpen={deleting !== null}
        namespace="payments"
        isPending={deletePayment.isPending}
        title={t('deleteModal.title')}
        message={t('deleteModal.message', {
          amount: Math.abs(deleting?.amount_hnt ?? 0).toFixed(4),
        })}
        /* Importierte Zahlungen sind schreibgeschuetzt (403 imported_readonly)
           und tragen deshalb gar keine Loeschen-Aktion mehr — hier landen nur
           noch manuell erfasste Zeilen. */
        onConfirm={() => {
          void handleConfirmDelete()
        }}
        onClose={() => setDeleting(null)}
      />
    </div>
  )
}
